import { useContext, useMemo } from "react";
import {
  ORDER_BY_TOKEN_QUERY,
  useOrderCreateMutation,
  useOrderUpdateMutation,
  useOrderByTokenQuery,
} from "@ticketingplatform/api/dist/order";
import useLocalStorage from "react-use/lib/useLocalStorage";
import { LOCAL_STORAGE_ORDER_KEY_PREFIX } from "setup/consts";
import { useApolloClient } from "@apollo/client";
import {
  OrderByToken,
  OrderByTokenVariables,
} from "@ticketingplatform/api/dist/order/__generated__/queries";
import { useEventOccurrenceContext } from "contexts/EventOccurrenceContext";

import { OrderValue } from "./types";
import { OrderContext } from "./context";

const baseOrderVariables: Partial<OrderByTokenVariables> = {
  ticketsInclude: true,
  ticketsTake: -1,
  discountCodeInclude: true,
};

export const useOrderContextValue = (): OrderValue => {
  const { cache } = useApolloClient();
  const { eventOccurrenceId } = useEventOccurrenceContext();
  const [orderToken, setOrderToken, removeOrderToken] = useLocalStorage<string>(
    `${LOCAL_STORAGE_ORDER_KEY_PREFIX}_${eventOccurrenceId}`,
    undefined,
    { raw: true }
  );

  const {
    data,
    loading: readLoading,
    error: readError,
  } = useOrderByTokenQuery(
    orderToken
      ? { variables: { ...baseOrderVariables, token: orderToken } }
      : { skip: true }
  );

  const [
    orderCreateMutation,
    { loading: createLoading, error: createError, reset: resetCreate },
  ] = useOrderCreateMutation({
    onCompleted({ orderCreate: newOrder }) {
      if (!newOrder?.token) {
        return;
      }

      cache.writeQuery<OrderByToken, OrderByTokenVariables>({
        query: ORDER_BY_TOKEN_QUERY,
        variables: {
          ...baseOrderVariables,
          token: newOrder.token,
        },
        data: {
          orderByToken: newOrder,
        },
      });

      setOrderToken(newOrder.token);
    },
  });

  const [
    orderUpdateMutation,
    { loading: updateLoading, error: updateError, reset: resetUpdate },
  ] = useOrderUpdateMutation();

  return useMemo<OrderValue>(
    () => ({
      loading: readLoading || createLoading || updateLoading,
      error: readError || createError || updateError,
      order: data?.orderByToken || undefined,
      async setOrder(input) {
        if (orderToken) {
          resetCreate();

          const updatedOrder = await orderUpdateMutation({
            variables: {
              ...baseOrderVariables,
              token: orderToken,
              input,
            },
          });

          return updatedOrder.data?.orderUpdate || undefined;
        }

        resetUpdate();

        const createdOrder = await orderCreateMutation({
          variables: {
            ...baseOrderVariables,
            input: {
              sendReceipt: false,
              autoCheckIn: true,
              ...input,
            },
          },
        });

        return createdOrder.data?.orderCreate || undefined;
      },
      async resetOrder() {
        resetUpdate();

        await orderCreateMutation({
          variables: {
            ...baseOrderVariables,
            input: {
              sendReceipt: false,
              autoCheckIn: true,
            },
          },
        });
      },
      removeOrder() {
        removeOrderToken();
      },
    }),
    [
      createError,
      createLoading,
      data?.orderByToken,
      orderCreateMutation,
      orderToken,
      orderUpdateMutation,
      readError,
      readLoading,
      removeOrderToken,
      resetCreate,
      resetUpdate,
      updateError,
      updateLoading,
    ]
  );
};

export const useOrderContext = (): OrderValue => {
  const orderContextValue = useContext(OrderContext);

  if (!orderContextValue) {
    throw new Error(
      "Make sure you are using `useOrderContext` hook inside `OrderProvider` provider"
    );
  }

  return orderContextValue;
};
